import {
    FGGridContainer,
    FGGridItem,
    FGIcons,
    FGLink,
    FGStack,
    FGTile,
    FGTypography,
} from '@fg/design-system';
import GQFlag from 'components/GQFlag';
import GQLoader from 'components/GQLoader/GQLoader';
import { IActiveCountry, IEvent, IEventUScore, VIEW } from 'interfaces';
import { filter, map } from 'lodash/fp';
import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { GQutils } from 'services/GQutils';
import { EventCircle } from './EventCircle';
import { CountryBadge } from './CountryBadge';
import SimilarArticles from 'components/SimilarArticles/SimilarArticles';
import PrimaryArticleBar from 'components/SimilarArticles/PrimaryArticleBar';
import { isSecondaryArticle, getClusterArticles } from './eventUtils';
import { InsightAndEventSeparator } from './InsightAndEventSeparator';

export { InsightAndEventSeparator };

type Props = {
    eventData: IEvent;
    eventText: string;
    isLoading: boolean;
    error: string;
    countriesById: { [countryID: number]: IActiveCountry };
};

const EventDisplay = React.memo(
    ({ eventData, isLoading, error, eventText, countriesById }: Props) => {
        const navigate = useNavigate();
        const getArticleUrl = (id: number) =>
            `/${VIEW.RISK_INTELLIGENCE}/events/${id}`;
        if (isLoading) {
            return <InsightAndEventLoadingContainer />;
        }
        if (error) {
            return (
                <FGStack justifyContent="center" style={{ marginTop: 32 }}>
                    <FGTypography variant="headline4" tag="label" color="strong">
                        {error}
                    </FGTypography>
                </FGStack>
            );
        }
        return (
            <InsightAndEventContainer>
                {isSecondaryArticle(eventData) && (
                    <PrimaryArticleBar
                        headline={eventData.cluster.representative_headline}
                        onBack={() =>
                            navigate(
                                getArticleUrl(
                                    eventData.cluster.representative_event_id
                                )
                            )
                        }
                    />
                )}
                <FGStack gap={4} style={{ flexWrap: 'wrap' }}>
                    {map(
                        (countryId: number) => {
                            const countryData: IActiveCountry =
                                countriesById[countryId];
                            return (
                                <CountryBadge
                                    country={countryData}
                                    key={countryId}
                                />
                            );
                        },
                        filter(
                            countryId => !!countriesById[countryId],
                            eventData.countries
                        )
                    )}
                </FGStack>
                <FGTypography
                    tag="h2"
                    variant="headline2"
                    color="strong"
                    cssStyle={{ marginBlock: 12 }}>
                    {eventData.headline}
                </FGTypography>
                <FGStack gap={8} alignItems="center">
                    <FGTypography tag="label" variant="body3-long" color="weak">
                        {eventData.time.format('MMM Do YY')}
                    </FGTypography>
                    <FGTypography tag="label" variant="body3-long" color="weak">
                        {/* todo: fix dot*/}•
                    </FGTypography>
                    <FGTypography tag="label" variant="body3-long" color="weak">
                        {eventData.source}
                    </FGTypography>
                    <FGLink
                        appearance="blue"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={eventData.url}>
                        <FGStack gap={8} alignItems="center">
                            <FGIcons size={12} icon="PopOut" />
                            <FGTypography
                                tag="label"
                                variant="link2"
                                cssStyle={{ cursor: 'pointer' }}>
                                See source article
                            </FGTypography>
                        </FGStack>
                    </FGLink>
                </FGStack>
                <SimilarArticles
                    articles={getClusterArticles(eventData)}
                    getArticleUrl={getArticleUrl}
                />
                <InsightAndEventSeparator />
                <FGGridContainer
                    isFluid
                    columnGap={0}
                    style={{ padding: 0, gap: 0 }}>
                    <FGGridItem sm={3}>
                        <GridHeaderColTypography>
                            Country
                        </GridHeaderColTypography>
                    </FGGridItem>
                    <FGGridItem sm={3}>
                        <GridHeaderColTypography>Risk</GridHeaderColTypography>
                    </FGGridItem>
                    <FGGridItem sm={3}>
                        <GridHeaderColTypography>
                            Impact
                        </GridHeaderColTypography>
                    </FGGridItem>
                    <FGGridItem sm={3}>
                        <GridHeaderColTypography>Date</GridHeaderColTypography>
                    </FGGridItem>
                    {map((uscore: IEventUScore) => {
                        const countryId = uscore.country_id;
                        const countryData = countriesById[countryId];
                        const impact = GQutils.findImpactByValue(uscore.impact);
                        return (
                            <React.Fragment key={uscore.score_id}>
                                <FGGridItem sm={3}>
                                    <FGStack gap={4} alignItems="center">
                                        <GQFlag
                                            countryId={countryId}
                                            size={16}
                                            style={{ marginRight: 8 }}
                                        />
                                        <GridItemTypography>
                                            {countryData.name}
                                        </GridItemTypography>
                                    </FGStack>
                                </FGGridItem>
                                <FGGridItem sm={3}>
                                    <FGStack alignItems="center">
                                        <GridItemTypography>
                                            {GQutils.findDimensionLongNameByID(
                                                uscore.dimension_id
                                            )}
                                        </GridItemTypography>
                                    </FGStack>
                                </FGGridItem>
                                <FGGridItem sm={3}>
                                    <FGStack gap={4} alignItems="center">
                                        <EventCircle color={impact.color} />
                                        <GridItemTypography>
                                            {impact.text}
                                        </GridItemTypography>
                                    </FGStack>
                                </FGGridItem>
                                <FGGridItem sm={3}>
                                    <FGStack alignItems="center">
                                        <GridItemTypography>
                                            {
                                                GQutils.findDurationByValue(
                                                    uscore.duration
                                                ).text
                                            }
                                        </GridItemTypography>
                                    </FGStack>
                                </FGGridItem>
                            </React.Fragment>
                        );
                    }, eventData.uscores)}
                </FGGridContainer>
                <InsightAndEventSeparator />
                <FGTypography tag="label" variant="body2">
                    {eventText}
                </FGTypography>
            </InsightAndEventContainer>
        );
    }
);

export const GridHeaderColTypography = React.memo(
    ({ children }: { children: React.ReactNode }) => {
        return (
            <FGTypography tag="label" variant="label3" color="weak">
                {children}
            </FGTypography>
        );
    }
);

export const GridItemTypography = React.memo(
    ({ children }: { children: React.ReactNode }) => {
        return (
            <FGTypography tag="label" variant="body4" color="strong">
                {children}
            </FGTypography>
        );
    }
);

//todo: rename all InsightAndEvent components - they are used by Events, Insights and Predictions.
export const InsightAndEventContainer = React.memo(
    ({ children }: { children: React.ReactNode }) => {
        return (
            <FGStack
                style={{
                    width: '100%',
                    height: '100%',
                    marginTop: 48,
                    position: 'relative',
                }}
                justifyContent="center"
                gap={0}>
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        justifyContent: 'center',
                    }}>
                    <FGStack style={{ width: 644 }} direction="column" gap={0}>
                        {children}
                    </FGStack>
                </div>
            </FGStack>
        );
    }
);


export const InsightAndEventBadge = React.memo(
    ({ children }: { children?: ReactNode }) => {
        return (
            <FGTile
                appearance="secondary"
                className="insight-and-event-badege-container">
                <FGStack gap={4}>{children}</FGStack>
            </FGTile>
        );
    }
);

export const InsightAndEventLoadingContainer = React.memo(() => {
    return (
        <div
            style={{
                width: '100%',
                height: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
            <GQLoader />
        </div>
    );
});

export default EventDisplay;
